import { gql } from '@apollo/client';
import { fragments as statisticsFragments } from './dashboard';

// Minimal fragment for schedule display (no expensive utilization subqueries)
const bookingDataSchedule = gql`
  fragment BookingDataSchedule on Booking {
    id
    roomId
    physician {
      id
      name
      color
    }
    organization {
      id
      name
    }
    startTime
    endTime
  }
`;

export const fragments = {
  bookingDataSchedule,
  // Full fragment with utilization data for block schedule analytics
  all: gql`
    fragment BookingData on Booking {
      id
      roomId
      physician {
        id
        name
        speciality
        category
        color
      }
      organization {
        id
        name
      }
      start
      startTime
      end
      endTime
      repeat
      endsAfterWeeks
      date
      notes
      masterBookingId
      createdAt
      utilization {
        percentage
        hasOutsideSlot
        outsideSlotDuration
        byPhysician {
          physicianId
          physician {
            id
            name
          }
          percentage
          hours
        }
      }
      actualUtilization {
        percentage
        hasOutsideSlot
        outsideSlotDuration
        byPhysician {
          physicianId
          physician {
            id
            name
          }
          percentage
          hours
        }
      }
      weekOfMonth
      occurrenceOfMonth
    }
  `,
};

export const bookingConfigurationQuery = gql`
  query bookingConfiguration {
    bookingConfiguration {
      dayStartTime
      dayEndTime
      turnoverTime
    }
  }
`;

export const list = gql`
  subscription bookings($from: Date!, $to: Date!) {
    bookings(from: $from, to: $to) {
      ...BookingData
    }
  }
  ${fragments.all}
`;

export const bookingStats = gql`
  subscription bookingStats($filter: Filter!) {
    bookingStats(filter: $filter) {
      ...StatisticsData
    }
  }
  ${statisticsFragments.all}
`;

export const physicianStats = gql`
  subscription physicianStats($filter: Filter!) {
    physicianStats(filter: $filter) {
      ...StatisticsData
    }
  }
  ${statisticsFragments.all}
`;

export const yearlyBookingStats = gql`
  query yearlyBookingStats($filter: Filter!) {
    yearlyBookingStats(filter: $filter) {
      id
      title
      unit
      value
      distribution {
        name
        value
        date
      }
    }
  }
`;

export const create = gql`
  mutation createBooking(
    $id: Long
    $masterBookingId: Long
    $roomId: Long!
    $physician: Long
    $organization: Long
    $date: Date!
    $fromMinute: Int!
    $endsAfterWeeks: Int
    $toMinute: Int!
    $repeat: Int
    $notes: String
    $speciality: String
    $weekOfMonth: Int
    $occurrenceOfMonth: [Int!]
  ) {
    createBooking(
      id: $id
      masterBookingId: $masterBookingId
      roomId: $roomId
      physician: $physician
      organizationId: $organization
      date: $date
      fromMinute: $fromMinute
      toMinute: $toMinute
      repeat: $repeat
      notes: $notes
      speciality: $speciality
      endsAfterWeeks: $endsAfterWeeks
      weekOfMonth: $weekOfMonth
      occurrenceOfMonth: $occurrenceOfMonth
    )
  }
`;

export const update = gql`
  mutation updateBooking(
    $id: Long
    $masterBookingId: Long
    $roomId: Long!
    $physician: Long
    $organization: Long
    $date: Date!
    $fromMinute: Int!
    $endsAfterWeeks: Int
    $toMinute: Int!
    $repeat: Int
    $notes: String
    $speciality: String
    $updateRest: Boolean
    $weekOfMonth: Int
    $occurrenceOfMonth: [Int!]
  ) {
    updateBooking(
      id: $id
      masterBookingId: $masterBookingId
      roomId: $roomId
      physician: $physician
      organizationId: $organization
      date: $date
      fromMinute: $fromMinute
      toMinute: $toMinute
      repeat: $repeat
      notes: $notes
      speciality: $speciality
      endsAfterWeeks: $endsAfterWeeks
      updateRest: $updateRest
      weekOfMonth: $weekOfMonth
      occurrenceOfMonth: $occurrenceOfMonth
    )
  }
`;

export const remove = gql`
  mutation removeBooking($id: Long!, $removeRest: Boolean) {
    removeBooking(id: $id, removeRest: $removeRest)
  }
`;

export const bookingConfigurationMutation = gql`
  mutation updateBookingConfiguration($dayStartTime: String, $dayEndTime: String, $turnoverTime: Int) {
    updateBookingConfiguration(dayStartTime: $dayStartTime, dayEndTime: $dayEndTime, turnoverTime: $turnoverTime)
  }
`;

export const openBlockAnalytics = gql`
  query openBlockAnalytics($filter: Filter!) {
    openBlockAnalytics(filter: $filter) {
      openBlockCount
      openBlockHours
      scheduledHours
      scheduledPercentage
      actualHours
      actualPercentage
      physicians {
        physicianId
        physician {
          id
          name
          speciality
        }
        scheduledHours
        scheduledPercentage
        actualHours
        actualPercentage
      }
    }
  }
`;

export const blockAnalytics = gql`
  query blockAnalytics($filter: Filter!, $aggregation: String) {
    blockAnalytics(filter: $filter, aggregation: $aggregation) {
      name
      allocated
      scheduled
      used
    }
  }
`;
